/**
 * Account Validation Query Hooks
 * Handles WhatsApp-based account validation flow
 */

import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'
import { API_ENDPOINTS } from '../endpoints'

// ============================================================================
// Query Keys
// ============================================================================

export const validationKeys = {
  all: ['validation'] as const,
  botContact: () => [...validationKeys.all, 'bot-contact'] as const,
  status: () => [...validationKeys.all, 'status'] as const,
}

// ============================================================================
// Query: Get Bot WhatsApp Contact Number
// ============================================================================

export interface BotContactResponse {
  contact: string
}

export function useBotContact() {
  return useQuery({
    queryKey: validationKeys.botContact(),
    queryFn: async () => {
      const response = await apiClient.get<BotContactResponse>(
        API_ENDPOINTS.users.getBotContact
      )
      return response.contact
    },
    staleTime: Infinity, // Bot contact number doesn't change
  })
}

// ============================================================================
// Query: Get Current Validation Status
// ============================================================================

export interface ValidationStatusResponse {
  validAccount: boolean
}

export function useValidationStatus() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: validationKeys.status(),
    queryFn: async () => {
      const response = await apiClient.get<ValidationStatusResponse>(
        API_ENDPOINTS.users.validationStatus
      )
      return response.validAccount
    },
    enabled: false, // Only fetch manually (used for polling)
  })

  // Automatically invalidate profile caches when validation status becomes true
  useEffect(() => {
    if (query.isSuccess && query.data === true) {
      // Invalidate auth profile cache
      void queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] })
      // Invalidate user profile cache (used by ProfilePage)
      void queryClient.invalidateQueries({ queryKey: ['userProfile'] })
    }
  }, [query.isSuccess, query.data, queryClient])

  return query
}

// ============================================================================
// Query: Get Validation Code for Authenticated User
// ============================================================================

export interface ValidationCodeResponse {
  validationCode: string
}

export function useValidationCode() {
  return useQuery({
    queryKey: [...validationKeys.all, 'code'] as const,
    queryFn: async () => {
      const response = await apiClient.get<ValidationCodeResponse>(
        API_ENDPOINTS.users.validationCode
      )
      return response.validationCode
    },
    enabled: false,
  })
}

// ============================================================================
// Mutation: Update Account Information
// ============================================================================

export interface UpdateAccountInfoData {
  name: string
  lastName: string
  email: string
  phoneNumber: string
  cityId: string
  gender?: 'male' | 'female' | ''
}

export function useUpdateAccountInfo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateAccountInfoData) => {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('lastName', data.lastName)
      formData.append('email', data.email)
      formData.append('phoneNumber', data.phoneNumber.replace(/\D/g, ''))
      formData.append('city', data.cityId)
      if (data.gender) {
        formData.append('gender', data.gender)
      }

      return apiClient.post(
        API_ENDPOINTS.users.updateAccountInformation,
        formData
      )
    },
    onSuccess: () => {
      // Invalidate user profile to refetch
      void queryClient.invalidateQueries({ queryKey: ['auth'] })
    },
  })
}

// ============================================================================
// Utility: Generate WhatsApp Validation Message
// ============================================================================

/**
 * Generates the exact validation message format used by the bot
 * Format: "Hola este es mi código de validación *{code}*"
 */
export function generateValidationMessage(validationCode: string | number): string {
  return `Hola este es mi código de validación *${validationCode}*`
}

/**
 * Opens WhatsApp chat with pre-filled message
 */
export function openWhatsAppChat(phoneNumber: string, message: string): void {
  const phone = phoneNumber.replace(/\D/g, '')
  const encodedMessage = encodeURIComponent(message)
  window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank')
}
